import { TooltipContent } from 'designSystem/components/Tooltip'
import styled from 'styled-components'

/** Inset for tab row; corner connectors are absolutely positioned and excluded. */
const TAB_BAR_INSET = '0.2rem'

export const TabCloseButtonSlot = styled.div`
  position: absolute;
  top: 1rem;
  right: 0.25rem;
  z-index: 40;
  display: flex;
  align-items: center;
  transform: translateY(-50%);
`

export const TabTitle = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  padding-right: 0.25rem;
  text-align: left;
`

export const TabTitleText = styled.span`
  display: block;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  mask-image: linear-gradient(to right, #000 calc(100% - 1rem), transparent);
  -webkit-mask-image: linear-gradient(
    to right,
    #000 calc(100% - 1rem),
    transparent
  );
`

export const DocumentTabTooltipContent = styled(TooltipContent)`
  background-color: #fff;
  color: #0b212d;
  animation: none;
  transform: none;

  svg {
    background-color: #fff;
    fill: #fff;
  }
`

export const TabShell = styled.div<{
  $active: boolean
  $dragging: boolean
  $renaming: boolean
}>`
  position: relative;
  flex: 0 1 10rem;
  min-width: ${({ $active, $renaming }) =>
    $active || $renaming ? '10rem' : '2.75rem'};
  max-width: 10rem;
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  transition: opacity 100ms ease;
`

export const TabAddButtonSlot = styled.div`
  display: flex;
  flex-shrink: 0;
  align-self: center;
`

export const TabDropIndicator = styled.div<{ $position: 'before' | 'after' }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $position }) =>
    $position === 'before' ? 'left: -1px;' : 'right: -1px;'}
  z-index: 20;
  width: 0.125rem;
  border-radius: var(--radius-sm, 0.25rem);
  background: var(--primary);
  pointer-events: none;
`

export const TabColorIndicator = styled.div<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 9999px;
  background: ${({ $color }) => $color};
`

export const TabIconSlot = styled.span`
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`

export const TabEditContainer = styled.div`
  position: relative;
  z-index: 10;
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  width: 100%;
  gap: 0.25rem;
  height: 2rem;
  min-width: 0;
  max-width: none;
  padding: 0.25rem;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.forecast.border};
  border-radius: var(--radius-md);
  background: ${({ theme }) => theme.forecast.background2_5};
  color: #fafafa;
  box-shadow:
    0 1px 2px -1px rgb(0 0 0 / 0.05),
    0 1px 4px -1px rgb(0 0 0 / 0.05);
`

export const Toolbar = styled.div`
  z-index: 0;
  display: flex;
  align-items: flex-end;
  padding: ${TAB_BAR_INSET} ${TAB_BAR_INSET} ${TAB_BAR_INSET} 0;
  background: ${({ theme }) => theme.forecast.background3};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: var(--radius) var(--radius) 0 0;
`

export const ToolbarAgentButtonSlot = styled.div`
  align-self: flex-start;
  display: flex;
  gap: 0.5rem;
`

export const PreviewModeSwitchButton = styled.button<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 1.5rem;
  padding: 0 0.5rem;
  border: 1px solid
    ${({ $checked }) =>
      $checked
        ? 'color-mix(in oklch, var(--primary) 40%, var(--border))'
        : 'var(--input)'};
  border-radius: 9999px;
  background: ${({ $checked }) =>
    $checked
      ? 'color-mix(in oklch, var(--primary) 10%, var(--background))'
      : 'color-mix(in oklch, var(--input) 30%, transparent)'};
  color: var(--foreground);
  box-shadow: var(--shadow-xs);
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  transition:
    background 100ms ease,
    border-color 100ms ease;

  &:hover {
    background: ${({ $checked }) =>
      $checked
        ? 'color-mix(in oklch, var(--primary) 15%, var(--background))'
        : 'color-mix(in oklch, var(--input) 50%, transparent)'};
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 2px;
  }
`

export const PreviewModeSwitchTrack = styled.span<{ $checked: boolean }>`
  position: relative;
  width: 1.5rem;
  height: 0.875rem;
  border-radius: 9999px;
  background: ${({ $checked }) =>
    $checked
      ? 'var(--primary)'
      : 'color-mix(in oklch, var(--muted-foreground) 30%, transparent)'};
  transition: background 100ms ease;
`

export const PreviewModeSwitchThumb = styled.span<{ $checked: boolean }>`
  position: absolute;
  top: 0.125rem;
  left: 0.125rem;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 9999px;
  background: var(--background);
  box-shadow: var(--shadow-xs);
  transform: translateX(${({ $checked }) => ($checked ? '0.625rem' : '0')});
  transition: transform 100ms ease;
`

export const TabsWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: ${TAB_BAR_INSET};
`
